/**
 * VirtualViolationList Component
 * Virtualized list for displaying violation cards with high performance
 */

import React, { useState, useMemo, type ReactNode } from 'react';
import { VirtualList } from './VirtualList';
import { Truck, AlertCircle, MapPin, FileText, Calendar, Wrench, Filter } from 'lucide-react';
import { getViolationByCode } from '../data/cfr-violations';

// Glow border styles for dark theme
const glowBorderStyle = (isOOS: boolean) => ({
  boxShadow: isOOS
    ? '0 0 15px rgba(220, 38, 38, 0.3), inset 0 0 8px rgba(220, 38, 38, 0.1)'
    : '0 0 15px rgba(34, 197, 94, 0.25), inset 0 0 8px rgba(34, 197, 94, 0.08)'
});

// Get severity color based on severity weight
const getSeverityColor = (severityWeight?: number): { border: string; glow: string; badge: string; text: string } => {
  if (!severityWeight) return { border: 'border-gray-500/80', glow: 'from-gray-500/10', badge: 'bg-gray-600', text: 'text-gray-300' };

  if (severityWeight >= 8) {
    // Critical (8-10)
    return { border: 'border-red-500/80', glow: 'from-red-500/10', badge: 'bg-red-600', text: 'text-red-300' };
  } else if (severityWeight >= 5) {
    // High (5-7)
    return { border: 'border-orange-500/80', glow: 'from-orange-500/10', badge: 'bg-orange-600', text: 'text-orange-300' };
  } else if (severityWeight >= 3) {
    // Medium (3-4)
    return { border: 'border-yellow-500/80', glow: 'from-yellow-500/10', badge: 'bg-yellow-600', text: 'text-yellow-300' };
  } else {
    // Low (1-2)
    return { border: 'border-green-500/80', glow: 'from-green-500/10', badge: 'bg-green-600', text: 'text-green-300' };
  }
};

// Get severity label
const getSeverityLabel = (severityWeight?: number): string => {
  if (!severityWeight) return 'Unknown';
  if (severityWeight >= 8) return 'Critical';
  if (severityWeight >= 5) return 'High';
  if (severityWeight >= 3) return 'Medium';
  return 'Low';
};

// Violation interface
interface Violation {
  inspViolationId?: string;
  violationCode?: string;
  violation_code?: string;
  violationDescription?: string;
  violation_description?: string;
  partNo?: string;
  partNoSection?: string;
  outOfServiceIndicator?: string;
  basicCategory?: string;
  basic_category?: string;
  severityWeight?: number;
  severity_weight?: number;
  inspViolUnit?: string;
  unitType?: number;
  allUnits?: Array<{ unitType: number }>;
  description?: string;
  vin?: string;
  unitMake?: string;
  inspectionDate?: string;
  inspDate?: string;
  inspectionState?: string;
  inspectionLocation?: string;
  citationNumber?: string;
  severity?: string;
  inspection_id?: number;
  inspectionId?: number;
}

interface VirtualViolationListProps {
  violations: Violation[];
  severityColor: string;
  height?: number | string;
  showPerformanceMetrics?: boolean;
  showFilters?: boolean;
  onVinClick?: (vin: string) => void;
  onInspectionClick?: (inspectionId: number) => void;
}

// Helper to get unit type label
const getUnitTypeLabel = (violation: Violation): string => {
  if (violation.inspViolUnit === 'D') return 'Driver';
  if (violation.unitType === 11) return 'Truck';
  if (violation.unitType === 9) return 'Trailer';
  if (violation.inspViolUnit && violation.inspViolUnit !== 'D') {
    const unitNum = parseInt(violation.inspViolUnit);
    if (violation.allUnits && violation.allUnits[unitNum - 1]) {
      const unit = violation.allUnits[unitNum - 1];
      if (unit.unitType === 11) return 'Truck';
      if (unit.unitType === 9) return 'Trailer';
    }
    return `Unit ${violation.inspViolUnit}`;
  }
  return 'Vehicle';
};

// Get enhanced description for violation
const getViolationDescription = (violation: Violation): string => {
  // Priority 1: Backend violationDescription field
  const backendDesc = violation.violationDescription || violation.violation_description;
  if (backendDesc && backendDesc !== 'N/A') return backendDesc;

  // Priority 2: Component description field
  if (violation.description && violation.description !== 'N/A' && violation.description !== 'No description available') {
    return violation.description;
  }

  // Priority 3: Look up from CFR violations data
  const code = violation.violationCode || violation.violation_code;
  if (code) {
    const cfrViolation = getViolationByCode(code);
    if (cfrViolation) return cfrViolation.description;
  }

  // Fallback
  return 'No description available';
};

// Premium Violation Card component - Dark Theme with Glow Border
const ViolationCard = ({ violation, severityColor, onVinClick, onInspectionClick }: { violation: Violation; severityColor: string; onVinClick?: (vin: string) => void; onInspectionClick?: (inspectionId: number) => void }) => {
  // Determine if Out of Service
  const isOOS = violation.outOfServiceIndicator === 'Y';

  // Get severity weight and color
  const severityWeight = violation.severityWeight || violation.severity_weight;
  const colors = getSeverityColor(severityWeight);
  const severityLabel = getSeverityLabel(severityWeight);

  // Get formatted date
  const dateField = violation.inspectionDate || violation.inspDate;
  const formattedDate = dateField
    ? new Date(dateField).toLocaleDateString()
    : 'N/A';

  // Get inspection ID
  const inspectionId = violation.inspectionId || violation.inspection_id;

  // Get unit type label
  const unitTypeLabel = getUnitTypeLabel(violation);

  // Get violation code
  const violationCode = violation.violationCode || violation.violation_code || (violation.partNo && violation.partNoSection ? `${violation.partNo}.${violation.partNoSection}` : 'N/A');

  // Get BASIC category
  const basicCategory = violation.basicCategory || violation.basic_category || 'Unknown';

  // Get enhanced description
  const description = getViolationDescription(violation);

  return (
    <div
      className={`relative w-full max-w-[340px] bg-[#0f172a] rounded-2xl border ${colors.border} overflow-hidden text-slate-300 shadow-2xl`}
      style={{
        boxShadow: `0 0 15px ${severityWeight && severityWeight >= 8 ? 'rgba(239, 68, 68, 0.3)' : severityWeight && severityWeight >= 5 ? 'rgba(249, 115, 22, 0.3)' : severityWeight && severityWeight >= 3 ? 'rgba(234, 179, 8, 0.3)' : 'rgba(34, 197, 94, 0.25)'}, inset 0 0 8px ${severityWeight && severityWeight >= 8 ? 'rgba(239, 68, 68, 0.1)' : severityWeight && severityWeight >= 5 ? 'rgba(249, 115, 22, 0.1)' : severityWeight && severityWeight >= 3 ? 'rgba(234, 179, 8, 0.1)' : 'rgba(34, 197, 94, 0.08)'}`
      }}
    >
      {/* Top Glow Effect */}
      <div className={`absolute top-0 left-0 right-0 h-24 bg-gradient-to-b ${colors.glow} to-transparent pointer-events-none`}></div>

      <div className="p-2 relative z-10">
        {/* Line 1: OOS + Severity + Code + Type Badge (all in one row) */}
        <div className="flex items-center gap-2 mb-1.5">
          {isOOS && (
            <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm tracking-wide uppercase">
              OOS
            </span>
          )}
          {severityWeight && (
            <span className={`${colors.badge} text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm tracking-wide uppercase`}>
              {severityLabel}
            </span>
          )}
          <span className="text-white text-lg font-semibold tracking-wide">
            {violationCode}
          </span>
          {/* Type Badge (RIGHT ALIGNED with ml-auto) */}
          <div className="flex items-center gap-1 ml-auto bg-slate-800/50 px-2 py-0.5 rounded text-xs text-slate-400 border border-slate-700/50">
            <Truck size={10} />
            <span>{unitTypeLabel}</span>
          </div>
        </div>

        {/* Line 2: Category + Severity Weight */}
        <div className="flex items-center gap-3 text-[11px] mb-3">
          <div className="flex items-center gap-1.5 text-slate-500">
            <Wrench size={10} />
            <span>{basicCategory}</span>
          </div>
          {severityWeight && (
            <div className={`flex items-center gap-1 ${colors.text}`}>
              <span>Weight: {severityWeight}</span>
            </div>
          )}
        </div>

        {/* Line 3: Description (NO LABEL) */}
        <div className="text-slate-100 text-[13px] font-medium leading-snug mb-2">
          {description}
        </div>

        {/* Line 4: VIN + Class Inline (NO LABELS) */}
        {(violation.vin || violation.unitMake) && (
          <div className="flex items-center gap-3 mb-4">
            {violation.vin && (
              <button
                onClick={(e) => { e.stopPropagation(); onVinClick?.(violation.vin!); }}
                className="font-mono text-xs text-blue-400 underline decoration-blue-500/30 underline-offset-2 tracking-wide"
              >
                {violation.vin}
              </button>
            )}
            {violation.unitMake && (
              <div className="text-xs text-slate-400 font-medium bg-slate-800/50 px-1.5 rounded border border-slate-700/50">
                {violation.unitMake}
              </div>
            )}
          </div>
        )}

        {/* Inspection Inner Card (darker background) */}
        <div className="bg-[#1e293b]/60 rounded-xl p-[6px] border border-slate-700/50">
          <div className="text-[10px] uppercase font-semibold tracking-wide text-slate-400 mb-1">
            Inspection Details ({violation.allUnits?.length || 1} units)
          </div>

          {/* Inspection # + Date on SAME LINE */}
          <div className="flex items-center gap-3 text-[11px] mb-0.5">
            {inspectionId && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  console.error('🚨🚨🚨 [VIRTUAL] INSPECTION CLICKED:', inspectionId, '🚨🚨🚨');
                  if (onInspectionClick) {
                    onInspectionClick(inspectionId);
                  } else {
                    console.warn('⚠️ No onInspectionClick callback provided');
                  }
                }}
                className="text-blue-400 hover:text-blue-300 underline underline-offset-2 font-medium"
              >
                #{inspectionId}
              </button>
            )}
            <span className="text-slate-500">Date: <span className="text-slate-300">{formattedDate}</span></span>
          </div>
          {violation.inspectionState && (
            <div className="text-[11px] text-slate-500">
              State: <span className="text-slate-300">{violation.inspectionState}</span>
            </div>
          )}

          {/* Location */}
          {violation.inspectionLocation && (
            <>
              <div className="border-t border-slate-700/50 my-1"></div>
              <div className="flex items-start gap-[3px] text-slate-300 text-[10px]">
                <MapPin size={10} className="text-slate-500 shrink-0 mt-0.5" />
                <span className="truncate opacity-80">{violation.inspectionLocation}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const VirtualViolationList: React.FC<VirtualViolationListProps> = ({
  violations,
  severityColor,
  height = 600,
  showPerformanceMetrics = false,
  showFilters = true,
  onVinClick,
  onInspectionClick,
}) => {
  // Filter state
  const [selectedBasicCategories, setSelectedBasicCategories] = useState<Set<string>>(new Set());
  const [selectedSeverities, setSelectedSeverities] = useState<Set<string>>(new Set());
  const [showOOSOnly, setShowOOSOnly] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  // Get unique BASIC categories and severities from violations
  const { basicCategories, severityLevels } = useMemo(() => {
    const categories = new Set<string>();
    const severities = new Set<string>();

    violations.forEach(v => {
      const category = v.basicCategory || v.basic_category;
      if (category) categories.add(category);

      const weight = v.severityWeight || v.severity_weight;
      if (weight) {
        severities.add(getSeverityLabel(weight));
      }
    });

    return {
      basicCategories: Array.from(categories).sort(),
      severityLevels: ['Critical', 'High', 'Medium', 'Low'].filter(s => severities.has(s))
    };
  }, [violations]);

  // Filter violations
  const filteredViolations = useMemo(() => {
    let filtered = violations;

    // Filter by BASIC category
    if (selectedBasicCategories.size > 0) {
      filtered = filtered.filter(v => {
        const category = v.basicCategory || v.basic_category;
        return category && selectedBasicCategories.has(category);
      });
    }

    // Filter by severity
    if (selectedSeverities.size > 0) {
      filtered = filtered.filter(v => {
        const weight = v.severityWeight || v.severity_weight;
        return weight && selectedSeverities.has(getSeverityLabel(weight));
      });
    }

    // Filter OOS only
    if (showOOSOnly) {
      filtered = filtered.filter(v => v.outOfServiceIndicator === 'Y');
    }

    return filtered;
  }, [violations, selectedBasicCategories, selectedSeverities, showOOSOnly]);

  // Toggle filter functions
  const toggleBasicCategory = (category: string) => {
    const newSet = new Set(selectedBasicCategories);
    if (newSet.has(category)) {
      newSet.delete(category);
    } else {
      newSet.add(category);
    }
    setSelectedBasicCategories(newSet);
  };

  const toggleSeverity = (severity: string) => {
    const newSet = new Set(selectedSeverities);
    if (newSet.has(severity)) {
      newSet.delete(severity);
    } else {
      newSet.add(severity);
    }
    setSelectedSeverities(newSet);
  };

  const clearFilters = () => {
    setSelectedBasicCategories(new Set());
    setSelectedSeverities(new Set());
    setShowOOSOnly(false);
  };

  const hasActiveFilters = selectedBasicCategories.size > 0 || selectedSeverities.size > 0 || showOOSOnly;

  const renderViolationList = () => {
    // Use virtual scrolling only for large datasets
    if (filteredViolations.length <= 100) {
      return (
        <div style={{ maxHeight: typeof height === 'number' ? `${height}px` : height, overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.5rem' }}>
            {filteredViolations.map((v, idx) => (
              <ViolationCard key={`${v.inspViolationId}-${idx}`} violation={v} severityColor={severityColor} onVinClick={onVinClick} onInspectionClick={onInspectionClick} />
            ))}
          </div>
        </div>
      );
    }

    return (
      <VirtualList
        items={filteredViolations}
        renderItem={(v, idx) => (
          <ViolationCard violation={v} severityColor={severityColor} onVinClick={onVinClick} onInspectionClick={onInspectionClick} />
        )}
        estimateSize={180}
        height={height}
        keyExtractor={(v, idx) => `${v.inspViolationId}-${idx}`}
        overscan={10}
        showPerformanceMetrics={showPerformanceMetrics}
        emptyMessage="No violations match your filters"
      />
    );
  };

  if (!showFilters) {
    return renderViolationList();
  }

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-slate-400" />
            <h3 className="text-base font-semibold text-white">Filters</h3>
            <span className="text-sm text-slate-400">
              ({filteredViolations.length} of {violations.length} violations)
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-blue-400 hover:text-blue-300 underline"
              >
                Clear all
              </button>
            )}
            <button
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              className="text-xs text-slate-400 hover:text-slate-300"
            >
              {showFiltersPanel ? 'Hide' : 'Show'} filters
            </button>
          </div>
        </div>

        {showFiltersPanel && (
          <div className="space-y-4">
            {/* OOS Toggle */}
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOOSOnly}
                  onChange={(e) => setShowOOSOnly(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600"
                />
                <span className="text-sm text-slate-300">Out-of-Service violations only</span>
              </label>
            </div>

            {/* Severity Filters */}
            {severityLevels.length > 0 && (
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 block">
                  Severity Level
                </label>
                <div className="flex flex-wrap gap-2">
                  {severityLevels.map(level => {
                    const isSelected = selectedSeverities.has(level);
                    const colorClass = level === 'Critical' ? 'bg-red-600 hover:bg-red-700' :
                                     level === 'High' ? 'bg-orange-600 hover:bg-orange-700' :
                                     level === 'Medium' ? 'bg-yellow-600 hover:bg-yellow-700' :
                                     'bg-green-600 hover:bg-green-700';
                    return (
                      <button
                        key={level}
                        onClick={() => toggleSeverity(level)}
                        className={`px-3 py-1 rounded text-xs font-semibold transition-all ${
                          isSelected
                            ? `${colorClass} text-white ring-2 ring-white/30`
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {level}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* BASIC Category Filters */}
            {basicCategories.length > 0 && (
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 block">
                  BASIC Category
                </label>
                <div className="flex flex-wrap gap-2">
                  {basicCategories.map(category => {
                    const isSelected = selectedBasicCategories.has(category);
                    return (
                      <button
                        key={category}
                        onClick={() => toggleBasicCategory(category)}
                        className={`px-3 py-1 rounded text-xs font-semibold transition-all ${
                          isSelected
                            ? 'bg-blue-600 text-white ring-2 ring-blue-400/30 hover:bg-blue-700'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Violations List */}
      {renderViolationList()}
    </div>
  );
};

export default VirtualViolationList;
